// Season View vocabulary.
//
// CANONICAL SOURCE IS `src/styles/design-system.css`. The values below are
// `var(--cl-*)` references, not literals, so there is exactly one place a
// colour is defined. Anything that needs a real hex (a gradient stop, a
// canvas) should read it from the stylesheet, not hardcode it here.

export const SEASON_VAR = {
  advent:       'var(--cl-season-advent)',
  christmas:    'var(--cl-season-christmas)',
  epiphany:     'var(--cl-season-epiphany)',
  lent:         'var(--cl-season-lent)',
  holyWeek:     'var(--cl-season-holy-week)',
  easter:       'var(--cl-season-easter)',
  pentecostDay: 'var(--cl-season-pentecost-day)',
  ordinary:     'var(--cl-season-ordinary)',
  feast:        'var(--cl-season-feast)',
} as const;

export type SeasonKey = keyof typeof SEASON_VAR;

/** The Day of Pentecost is one Sunday and is red. The season after it is green. */
const PENTECOST_DAY = /^(the\s+)?day of pentecost|^pentecost sunday$|^pentecost$/i;

/**
 * Map a season as stored in liturgical_calendar onto the palette. `isSpecial`
 * wins — a feast displaces the season it lands in, which is the whole reason
 * St Luke reads gold in the middle of a green autumn.
 */
export function seasonKey(
  season: string | null,
  isSpecial: boolean | null,
  liturgyName?: string | null,
): SeasonKey {
  if (liturgyName && PENTECOST_DAY.test(liturgyName.trim())) return 'pentecostDay';
  if (isSpecial) return 'feast';
  const s = (season || '').toLowerCase();
  if (s.startsWith('advent')) return 'advent';
  if (s.startsWith('christmas')) return 'christmas';
  if (s.startsWith('holy week')) return 'holyWeek';
  if (s.startsWith('lent')) return 'lent';
  if (s.startsWith('easter')) return 'easter';
  if (s.startsWith('epiphany')) return 'epiphany';
  return 'ordinary'; // the long season after Pentecost
}

/**
 * Ask status. One monospace glyph plus a colour — deliberately not a second
 * badge family. Gold on `asked` is the point of the lane: an unanswered ask
 * should be the warmest mark in the column.
 */
export const STATUS = {
  idea:      { mark: '·', color: 'var(--cl-status-idea)',      label: 'idea' },
  asked:     { mark: '○', color: 'var(--cl-status-asked)',     label: 'asked' },
  accepted:  { mark: '◐', color: 'var(--cl-status-accepted)',  label: 'accepted' },
  confirmed: { mark: '●', color: 'var(--cl-status-confirmed)', label: 'confirmed' },
  declined:  { mark: '×', color: 'var(--cl-status-declined)',  label: 'declined' },
  cancelled: { mark: '×', color: 'var(--cl-status-cancelled)', label: 'cancelled' },
  inhouse:   { mark: '',  color: 'var(--cl-status-inhouse)',   label: 'in-house' },
} as const;

export type StatusKey = keyof typeof STATUS;

/** Progression order, terminal states last. */
export const STATUS_ORDER: StatusKey[] = [
  'idea', 'asked', 'accepted', 'confirmed', 'declined', 'cancelled', 'inhouse',
];

/** Struck-through names: the ask is dead, but the row stays as a record. */
export const TERMINAL: StatusKey[] = ['declined', 'cancelled'];

type Badge = { label: string; className: string };

/**
 * performance_type as stored ("Choir Anthem", "Congregational Hymn", "Solo",
 * "Instrumental", "Bell Choir") onto an outlined badge. Congregational hymns
 * get no badge — they are the default, and badging the default is noise.
 *
 * Every badge here is OUTLINED. The one filled badge in the system is "at the
 * keyboard", and it stays rare so it stays scannable.
 */
export function badgeFor(performanceType: string | null | undefined): Badge | null {
  const pt = (performanceType || '').toLowerCase();
  if (!pt || pt.includes('congregation')) return null;
  if (pt.includes('bell')) return { label: 'bell', className: 'sv-badge--bell' };
  if (pt.includes('choir') || pt.includes('anthem')) return { label: 'choir', className: 'sv-badge--choir' };
  if (pt.includes('band')) return { label: 'band', className: 'sv-badge--band' };
  if (pt.includes('organ')) return { label: 'organ', className: 'sv-badge--organ' };
  if (pt.includes('solo')) return { label: 'solo', className: 'sv-badge--solo' };
  if (pt.includes('cantor')) return { label: 'cantor', className: 'sv-badge--solo' };
  if (pt.includes('child')) return { label: 'children', className: 'sv-badge--children' };
  if (pt.includes('instrument')) return { label: 'inst', className: 'sv-badge--organ' };
  return { label: pt.split(' ')[0], className: 'sv-badge--organ' };
}
